import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate, useLocation } from "react-router";

import Preview from "./component/menuBoard/preview";
import TabMenu from "./component/menuBoard/tabMenu";
import Address from "./component/menuBoard/addr";
import Products from "./component/menuBoard/products";
import Details from "./component/menuBoard/details";
import Product from "./component/menuBoard/product";

import { MenuContainer, Container } from "./component/styled/setting";
import { useDisableScroll } from "../../hooks/useDisableScroll";

const MenuBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cmpnyNo } = useParams();

  const previewRef = useRef(null);

  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [menuGroups, setMenuGroups] = useState([]);
  const [info, setInfo] = useState({ image: [], text: null });
  const [product, setProduct] = useState(null);
  const [savedScrollTop, setSavedScrollTop] = useState(0);

  useDisableScroll();

  const isTimestampExpired = () => {
    if (!cmpnyNo) throw new Error("회사번호 없음");
    try {
      const raw = localStorage.getItem(`timestamp:${cmpnyNo}`);
      const saved = raw ? parseInt(raw, 10) : null;

      if (!saved || !Number.isFinite(saved)) throw new Error("세션 만료 시간 없음");
      const elapsed = Date.now() - saved;
      const expired = elapsed > 5 * 60 * 1000;

      if (expired) {
        try {
          alert("세션이 만료되었습니다. QR을 다시 찍어주세요.");
          localStorage.removeItem(`mbOrderItems:${cmpnyNo}`);
          localStorage.removeItem(`mbTableNo:${cmpnyNo}`);
          localStorage.removeItem(`mbCart:${cmpnyNo}`);

          try {
            window.open("", "_self");
            window.close();
          } catch (e) {}
          navigate("/");
        } catch (e) {}
      }
      return expired;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const hasCartItems = () => {
    if (!cmpnyNo) return false;
    try {
      const raw = localStorage.getItem(`mbCart:${cmpnyNo}`);
      const arr = JSON.parse(raw || "[]");
      return Array.isArray(arr) && arr.length > 0;
    } catch (e) {
      return false;
    }
  };

  const getCartCount = () => {
    if (!cmpnyNo) return 0;
    try {
      const raw = localStorage.getItem(`mbCart:${cmpnyNo}`);
      const arr = JSON.parse(raw || "[]");
      if (!Array.isArray(arr)) return 0;
      return arr.reduce((sum, item) => sum + (item.quantity || 1), 0);
    } catch (e) {
      return 0;
    }
  };

  useEffect(() => {
    if (!cmpnyNo) return;

    const urlParams = new URLSearchParams(location.search);
    const tableNo = urlParams.get("tableNo");
    if (tableNo) {
      localStorage.setItem(`mbTableNo:${cmpnyNo}`, tableNo);
    }

    getCompany();
  }, [cmpnyNo, location.search]);

  const decryptId = async (id) => {
    const url = "/etc/decrypt-6char";
    const body = { code: id };
    const res = await axios.post(url, body);
    return res.data.plain;
  };

  const getCompany = async () => {
    const decryptedId = await decryptId(cmpnyNo);
    const url = "/erp/ss/company/get";
    const body = { cmpnyNo: decryptedId };
    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        const data = res.data;
        console.log(data);
        setCompany(data);
        if (data) {
          await fetchInitialData();
        }
      }
    } catch (error) {
      console.error("회사 정보 로딩 실패", error);
      navigate("/");
    }
  };

  // 초기 데이터 조회
  const fetchInitialData = async () => {
    try {
      Promise.all([getMenuGroup()]);
    } catch (error) {
      console.error("초기 데이터 로딩 실패", error);
    }
  };

  // 메뉴 그룹 목록 요청 및 정렬
  const getMenuGroup = async () => {
    try {
      const decryptedId = await decryptId(cmpnyNo);
      if (!decryptedId) throw new Error("회사번호 없음");

      const url = "/erp/menuGroup/get";
      const body = { cmpnyNo: decryptedId, menuPlatform: "PICKUP", admin: false };
      const res = await axios.post(url, body);

      if (res.status) {
        const data = res?.data || [];
        setMenuGroups(
          [...data].sort((a, b) => {
            if (a.sortOrder !== b.sortOrder) {
              return a.sortOrder - b.sortOrder;
            }
            return Number(b.id) - Number(a.id);
          }),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toggleProduct = (item = null) => {
    if (isTimestampExpired()) return;
    const container = previewRef.current;
    if (!container) return;

    if (!item) {
      const target = savedScrollTop || 0;
      setProduct(null);
      requestAnimationFrame(() => {
        container.scrollTo({ top: target, behavior: "auto" });
      });
      return;
    }

    if (!item.price) {
      toast.error("구매할 수 없는 상품입니다.");
      return;
    }

    if (item.status === "SOLDOUT") {
      toast.error("품절된 상품입니다.");
      return;
    }

    setSavedScrollTop(container.scrollTop || 0);
    container.scrollTop = 0;
    setProduct(item);
  };

  return (
    <Container>
      <MenuContainer ref={previewRef} style={{ overflowY: product ? "hidden" : "auto" }}>
        <Preview company={company} image={company?.cmpnyImage} info={company?.cmpnyNm} />
        <TabMenu activeTab={activeTab} setActiveTab={setActiveTab} />
        <div className="slideWrapper">
          <div className={`slideContent ${!activeTab ? "active" : "inactive"}`}>
            <Address company={company} />
            <Products menuGroups={menuGroups} toggleProduct={toggleProduct} />
          </div>
          <div className={`slideContent ${activeTab ? "active" : "inactive"}`}>
            <Details info={info} company={company} />
          </div>
          <Product product={product} toggleProduct={toggleProduct} />
        </div>
        {!activeTab && hasCartItems() && (
          <button
            className="submit"
            onClick={() => !isTimestampExpired() && navigate(`/menu-board/${cmpnyNo}/cart`)}>
            {`카트보기 (${getCartCount()})`}
          </button>
        )}
      </MenuContainer>
    </Container>
  );
};

export default MenuBoard;
